import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, AudioWaveform } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { LanguageSelector } from "./LanguageSelector";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/order", label: "Order", testid: "nav-link-order" },
  { to: "/private-events", label: "Private & Events", testid: "nav-link-private-events" },
  { to: "/business", label: "Business", testid: "nav-link-business" },
  { to: "/album", label: "Album", testid: "nav-link-album" },
  { to: "/contact", label: "Contact", testid: "nav-link-contact" },
];

export default function Navbar() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-30 border-b border-[#282C3D] bg-[#0B0C10]/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5" onClick={() => setOpen(false)}>
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#E5A93C] text-[#0B0C10]">
            <AudioWaveform size={18} />
          </span>
          <span className="leading-tight">
            <span className="block font-display text-base font-bold tracking-tight text-slate-50">EVI-STUDIO</span>
            <span className="block font-mono text-[9px] uppercase tracking-widest text-slate-500">by Evinet.group</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-0.5 2xl:flex" aria-label={t("Main navigation")}>
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `rounded-full px-2.5 py-2 text-sm transition-colors duration-200 ${
                  isActive ? "bg-[#1A1D2B] text-[#FFC857]" : "text-slate-400 hover:text-slate-100"
                }`
              }
            >
              {t(l.label)}
            </NavLink>
          ))}
          <Link to="/order" data-testid="nav-order-cta" className="btn-primary ml-3 !px-5 !py-2">
            {t("Order Your Song")}
          </Link>
        </nav>

        <div className="flex shrink-0 items-center gap-3">
        <LanguageSelector />
        <button
          data-testid="nav-mobile-menu-button"
          className="flex h-10 w-10 items-center justify-center text-slate-300 transition-colors hover:text-[#FFC857] 2xl:hidden"
          onClick={() => setOpen((o) => !o)}
          aria-label={t("Toggle menu")}
          aria-expanded={open}
          aria-controls="mobile-navigation"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
        </div>
      </div>

      {open && (
        <nav id="mobile-navigation" data-testid="nav-mobile-menu" className="max-h-[70vh] overflow-y-auto border-t border-[#282C3D] bg-[#0E1018] px-4 py-4 2xl:hidden" aria-label={t("Mobile navigation")}>
          <div className="flex flex-col gap-1">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-${l.testid}`}
                onClick={() => setOpen(false)}
                className={`rounded-lg px-4 py-3 text-sm transition-colors duration-200 ${
                  location.pathname === l.to ? "bg-[#1A1D2B] text-[#FFC857]" : "text-slate-300 hover:bg-[#1A1D2B]"
                }`}
              >
                {t(l.label)}
              </Link>
            ))}
            <Link to="/order" data-testid="nav-mobile-order-cta" onClick={() => setOpen(false)} className="btn-primary mt-2">
              {t("Order Your Song")}
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}
